import { RightOutlined, LinkOutlined } from '@ant-design/icons';
import { Divider, Typography, List as AntList, Row } from 'antd';
import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Link, useRouteMatch } from 'react-router-dom';
import styled from 'styled-components';
import { t, jt } from 'ttag';

import Loading from 'common/components/Loading';
import { EVENTMOBI_URLS, SUPPORT_URLS } from 'common/constants/ExternalUrls';
import { unsetOrganization } from 'common/state/organization';
import { getFirstName, getLastName, getEmail, getIsStaff } from 'common/state/user';

import { list as listFn } from './service';

const { Title: AntTitle, Text } = Typography;

const MANAGEMOBI_ROUTE = '/managemobi';

/*
Note: EXP is available in more languages than just English and German,
but only those two are defined here because the support site is currently
only in EN and DE.
Portuguese also exists in Support but it's completely empty.
Missing languages fallback to EN.
*/

const Title = styled(AntTitle)`
  margin-top: ${props => props.$heading && '52px'};
  && {
    color: #595959;
    font-weight: 400;
  }
`;

const Item = styled(AntList.Item)`
  padding-left: 16px;
  padding-right: 16px;
  height: 54px;
  width: 450px;
  border: 1px solid #e8e8e8;
  cursor: pointer;
  margin-top: ${props => props.$footer && '32px'};

  a {
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: space-between;
    color: rgba(0, 0, 0, 0.85);
    text-decoration: none;
  }

  h4 {
    margin-bottom: 0;
  }

  &&& {
    border-bottom: ${props => (props.$footer || props.$lastItem ? '1px solid #e8e8e8' : '0')};
  }
`;

const Footer = styled(AntList.Item)`
  margin-top: 16px;
  font-size: 12px;
  .ant-list-item-meta-title {
    font-size: 12px;
    color: #595959;
  }
`;

function List() {
  const dispatch = useDispatch();
  const { url } = useRouteMatch();

  const isStaff = useSelector(getIsStaff);
  const firstName = useSelector(getFirstName);
  const lastName = useSelector(getLastName);
  const email = useSelector(getEmail);
  const displayName = firstName || lastName ? [firstName, lastName].join(' ') : email;

  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function effect() {
      try {
        const result = await listFn();
        setList(result);
      } finally {
        dispatch(unsetOrganization());
        setLoading(false);
      }
    }

    effect();
  }, [dispatch]);

  if (loading) {
    return <Loading />;
  }

  const EMLink = (
    <a
      key="em"
      href={EVENTMOBI_URLS.HOME_WITH_UTM}
      rel="nofollow noopener noreferrer"
      target="_blank"
    >
      EventMobi
    </a>
  );

  return (
    <>
      <Row type="flex" justify="center">
        <Title $heading level={2}>{t`Hello ${displayName}`}</Title>
      </Row>
      <Row type="flex" justify="center">
        <Title level={5}>{t`Welcome back to the EventMobi Experience Manager`}</Title>
      </Row>
      <Divider />
      <Row type="flex" justify="center">
        <Title level={5}>{t`To get started, please select an organization:`}</Title>
      </Row>
      <Row type="flex" justify="center">
        <AntList
          dataSource={list}
          renderItem={(item, index) => (
            <Link to={`${url}/${item?.id}`}>
              <Item $lastItem={index === list.length - 1}>
                <Item.Meta title={item.name} />
                <RightOutlined />
              </Item>
            </Link>
          )}
          footer={
            <>
              {isStaff && (
                <Link to={MANAGEMOBI_ROUTE}>
                  <Item $footer>
                    <Item.Meta title={<Text style={{ color: '#f5222d' }}>{t`ManageMobi`}</Text>} />
                    <RightOutlined />
                  </Item>
                </Link>
              )}
              <Divider />
              <a href={SUPPORT_URLS.WHATS_NEW} target="_blank" rel="nofollow noopener noreferrer">
                <Item>
                  <Item.Meta title={t`New Features & Updates`} />
                  <LinkOutlined />
                </Item>
              </a>
              <a
                href={SUPPORT_URLS.KNOWLEDGE_BASE}
                target="_blank"
                rel="nofollow noopener noreferrer"
              >
                <Item $lastItem>
                  <Item.Meta title={t`EventMobi Knowledge Base`} />
                  <LinkOutlined />
                </Item>
              </a>
              <Footer>
                <Item.Meta
                  title={
                    <>
                      <a
                        href={EVENTMOBI_URLS.TERMS_OF_USE}
                        rel="nofollow noopener noreferrer"
                        target="_blank"
                      >
                        {t`Terms of Use`}
                      </a>
                      <Divider type="vertical" />
                      <a
                        href={EVENTMOBI_URLS.PRIVACY_POLICY}
                        rel="nofollow noopener noreferrer"
                        target="_blank"
                      >
                        {t`Privacy Policy`}
                      </a>
                    </>
                  }
                />
                <div>{jt`Powered by ${EMLink}™`}</div>
              </Footer>
            </>
          }
        />
      </Row>
    </>
  );
}

export default List;
